/**
 * Actualités (articles WordPress), contacts du comité (membres) et équipe
 * publique (site), réservation membre du club-house.
 */
import React, { useState } from 'react';
import { api } from '../lib/api.js';
import { Spinner, ErrorBox, OfflineNote, Badge, useApi, Intro, IconeLieu } from '../components/common.jsx';
import { fmtDate, fmtCHF, capitaliser } from '../lib/format.js';
import { ClubhousePublic } from './PublicViews.jsx';

/**
 * Ordre protocolaire des désignations (miroir de
 * Capabilities::DESIGNATIONS_COMITE — ceinture de sécurité : le serveur trie
 * déjà, mais un cache hors ligne antérieur peut arriver non trié).
 */
const ORDRE_TITRES = ['Présidence', 'Vice-Présidence', 'Responsable des finances',
  'Responsable du matériel', 'Responsable des terrains', 'Responsable des entraînements',
  'Responsable des animations', 'Responsable des activités', 'Membre du comité'];
const rangTitre = (t) => { const i = ORDRE_TITRES.indexOf(t); return i === -1 ? ORDRE_TITRES.length : i; };

/** Actualités du club dans l'application. */
export function Actualites() {
  const [state] = useApi('news', { cache: 'news' });
  const [ouvert, setOuvert] = useState(null);
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const posts = state.data.posts || [];
  return (
    <div>
      <h2 className="h4 mb-2">Actualités du club</h2>
      <Intro>Les dernières nouvelles publiées par le club. Touchez un article pour le lire.</Intro>
      <OfflineNote result={state} />
      {posts.length === 0 && <p className="text-muted">Aucune actualité pour le moment.</p>}
      <div className="row g-3">
        {posts.map((p) => (
          <div className="col-md-6" key={p.id}>
            <div className="card h-100">
              {p.image && <img src={p.image} className="card-img-top" alt="" style={{ maxHeight: 190, objectFit: 'cover' }} />}
              <div className="card-body" role="button" onClick={() => setOuvert(ouvert === p.id ? null : p.id)}>
                <h5 className="card-title mb-1">{p.titre}</h5>
                <p className="text-muted small mb-0">{fmtDate(p.date)}</p>
                {ouvert === p.id && <div className="mt-3" dangerouslySetInnerHTML={{ __html: p.contenu }} />}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Liste de contacts du comité (membres connectés). */
export function ContactsComite() {
  const [state] = useApi('comite', { cache: 'comite' });
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const membres = [...state.data].sort((a, b) => rangTitre(a.titre) - rangTitre(b.titre));
  return (
    <div>
      <h2 className="h4 mb-2">Le comité</h2>
      <Intro>Les membres du comité et leurs coordonnées, présentés par ordre protocolaire.</Intro>
      <OfflineNote result={state} />
      <div className="list-group">
        {membres.map((m, i) => (
          // Coordonnées SOUS le nom : la troisième colonne alignée à droite
          // poussait l'e-mail hors de l'écran sur mobile.
          <div className="list-group-item d-flex align-items-start gap-3" key={i}>
            {m.photo && <img src={m.photo} alt="" width="48" height="48" className="rounded-circle flex-shrink-0" />}
            {/* minWidth:0 : sans lui, text-truncate est inopérant dans un flex item. */}
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <strong>{m.nom}</strong>
              <div><small className="text-muted">{m.titre}</small></div>
              <div className="d-flex flex-column flex-sm-row column-gap-3">
                <a href={'mailto:' + m.email} className="small text-truncate">{m.email}</a>
                {m.telephone && <a href={'tel:' + m.telephone} className="small">{m.telephone}</a>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Équipe publique pour le site (shortcode [adlt_equipe]) : nom et titre. */
export function Equipe() {
  const [state] = useApi('equipe', {});
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  const membres = [...state.data].sort((a, b) => rangTitre(a.titre) - rangTitre(b.titre));
  return (
    <div className="row g-3">
      {membres.map((m, i) => (
        <div className="col-6 col-md-4 col-lg-3" key={i}>
          <div className="card h-100 text-center">
            <div className="card-body">
              {m.photo && <img src={m.photo} alt="" width="96" height="96" className="rounded-circle mb-2" />}
              <h6 className="mb-0">{m.nom}</h6>
              <small className="text-muted">{m.titre}</small>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Groupes d'entraînement, horaires et lieux — page publique du site
 * (shortcode `[adlt_groupes_horaires]`). Aucune donnée personnelle.
 *
 * Le club change de lieu selon la saison : un même groupe a donc plusieurs
 * créneaux. Celui qui s'applique aujourd'hui est mis en avant, les suivants
 * sont datés — sans quoi le visiteur lit deux horaires contradictoires.
 */
export function GroupesHoraires() {
  const [state] = useApi('groupes-horaires', { cache: 'groupes_horaires' });
  if (state.loading) return <Spinner />;
  if (!state.ok) return <ErrorBox message={state.error} />;
  if (!state.data.length) return <p className="text-muted">Aucun groupe d'entraînement pour le moment.</p>;
  return (
    <div className="row g-3">
      {state.data.map((g) => (
        <div className="col-md-6 col-lg-4" key={g.id}>
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title mb-1">{g.nom}</h5>
              {(g.categorie_age || g.discipline) && (
                <p className="text-muted small mb-2">
                  {g.categorie_age}{g.categorie_age && g.discipline ? ' · ' : ''}
                  {g.discipline !== 'mixte' ? g.discipline : ''}
                </p>
              )}
              {g.horaires.length === 0 ? (
                <p className="text-muted small mb-0">Horaire à confirmer.</p>
              ) : (
                <ul className="list-unstyled mb-0">
                  {g.horaires.map((h, i) => (
                    <li key={i} className={i > 0 ? 'mt-2' : ''}>
                      <strong>{capitaliser(h.jour)} {h.heure_debut}–{h.heure_fin}</strong>
                      {h.lieu && (
                        <><br /><small className="text-muted d-inline-flex align-items-center gap-1">
                          <IconeLieu taille={12} /> {h.lieu}
                        </small></>
                      )}
                      {!h.en_cours && (
                        <><br /><small className="text-muted">dès le {fmtDate(h.date_debut)}</small></>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Locations (membres) : réservation du club-house (journée / demi-journée). */
export function ClubhouseMembre() {
  return (
    <div>
      <Intro>Réservez le club-house pour une journée ou une demi-journée.
        Chaque demande est soumise à la validation du comité.</Intro>
      <ClubhousePublic />
      <p className="text-muted small mt-3">Retrouvez et annulez vos demandes dans <a href="#/mes-reservations">Personnel → Mes réservations</a>.</p>
    </div>
  );
}

/** Mes réservations : suivi et annulation par le membre. */
export function MesReservations({ toast }) {
  const [resas, reload] = useApi('reservations', { deps: [] });

  async function annuler(id) {
    if (!confirm('Annuler cette réservation ?')) return;
    const r = await api('reservations/' + id, { method: 'DELETE' });
    if (r.ok) { toast('Réservation annulée.'); reload(); } else toast(r.error, true);
  }

  const FORMULES = { journee: 'Journée', demi_matin: 'Demi-journée (matin)', demi_apres_midi: 'Demi-journée (après-midi)' };
  const miennes = (resas.ok ? resas.data : []).filter((r) => !r.demandeur);

  return (
    <div>
      <h2 className="h4 mb-2">Mes réservations</h2>
      <Intro>Vos prochaines réservations du club-house sont affichées sur cette page.
        Vous pouvez les annuler tant que la date n'est pas passée.</Intro>
      {resas.loading && <Spinner />}
      <div className="list-group">
        {!resas.loading && miennes.length === 0 && <div className="text-muted p-2">Aucune réservation.</div>}
        {miennes.map((r) => (
          <div key={r.id} className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2">
            <span>
              <strong>{fmtDate(r.debut)}</strong> — {FORMULES[r.formule] || (String(r.debut).slice(11, 16) + '–' + String(r.fin).slice(11, 16))}
              {r.prix !== null && r.prix !== undefined && <> · {fmtCHF(r.prix)}</>}
              {r.commentaire_comite && <><br /><small className="text-muted">Comité : {r.commentaire_comite}</small></>}
            </span>
            <span className="d-flex align-items-center gap-2">
              <Badge statut={r.statut} />
              {['demandee', 'validee'].includes(r.statut) && (
                <button className="btn btn-outline-danger btn-sm" onClick={() => annuler(r.id)}>Annuler</button>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
